import type {
  UserWorkAnchor,
  UserWorkExtra,
  UserWorkItem,
  UserWorkRelCartoon,
  UserWorkUser,
} from '@/api/home/type'
import type { SearchHotWordItem, SearchWorkItem } from '@/api/search'

export interface SearchHotWordOption {
  id: string
  label: string
  keyword: string
}

const EMPTY_FROM_ID = '000000000000000000000000'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function toNumber(value: unknown, fallback = 0) {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function toStringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null
}

function normalizeRelCartoons(item: SearchWorkItem): UserWorkRelCartoon[] {
  const relCartoons = (item.relCartoons ?? [])
    .filter((cartoon): cartoon is Partial<UserWorkRelCartoon> => isRecord(cartoon))
    .map((cartoon) => ({
      id: toStringOrNull(cartoon.id) ?? '',
      name: toStringOrNull(cartoon.name) ?? '',
      avatar: toStringOrNull(cartoon.avatar) ?? '',
      banner: toStringOrNull(cartoon.banner),
      gender: toStringOrNull(cartoon.gender),
      summary: toStringOrNull(cartoon.summary),
      self: typeof cartoon.self === 'number' ? cartoon.self : null,
      type: toStringOrNull(cartoon.type),
    }))
    .filter((cartoon) => cartoon.id && cartoon.name && cartoon.avatar)

  if (relCartoons.length > 0) {
    return relCartoons
  }

  const cartoonId = toStringOrNull(item.cartoonId)
  const cartoonName = toStringOrNull(item.cartoonName)
  const cartoonAvatar = toStringOrNull(item.cartoonAvatar)

  if (!cartoonId || !cartoonName || !cartoonAvatar) {
    return []
  }

  return [
    {
      id: cartoonId,
      name: cartoonName,
      avatar: cartoonAvatar,
      banner: null,
      gender: null,
      summary: null,
      self: 1,
      type: null,
    },
  ]
}

function normalizeUser(user: SearchWorkItem['user']): UserWorkUser | null {
  if (!isRecord(user) || !user.id) {
    return null
  }

  return {
    id: String(user.id),
    uid: toStringOrNull(user.uid) ?? '',
    identity: toNumber(user.identity),
    nickname: toStringOrNull(user.nickname) ?? '',
    avatar: toStringOrNull(user.avatar) ?? '',
    avatarFrame: toStringOrNull(user.avatarFrame),
    identitys: Array.isArray(user.identitys)
      ? user.identitys.filter((item): item is string => typeof item === 'string')
      : null,
  }
}

function normalizeAnchor(item: SearchWorkItem): UserWorkAnchor {
  const anchor = isRecord(item.anchor) ? item.anchor : null
  const anchorId = toStringOrNull(anchor?.id) ?? item.id
  const anchorJump =
    toStringOrNull(anchor?.jump) ??
    toStringOrNull(item.jump) ??
    (item.obj === 'radio' ? 'radio' : item.obj === 'media' ? 'cartoon_box' : 'pipe')

  return {
    id: anchorId,
    jump: anchorJump,
  }
}

function normalizeExtra(extra: unknown): UserWorkExtra {
  return isRecord(extra) ? extra : {}
}

export function normalizeSearchWorkItem(item: SearchWorkItem): UserWorkItem {
  const anchor = normalizeAnchor(item)

  return {
    id: item.id,
    obj: toStringOrNull(item.obj) ?? 'pipe',
    fromId: toStringOrNull(item.fromId) ?? EMPTY_FROM_ID,
    name: toStringOrNull(item.name) ?? '未命名作品',
    summary: toStringOrNull(item.summary),
    cover: toStringOrNull(item.cover) ?? '',
    useNum: toNumber(item.useNum),
    shareNum: toNumber(item.shareNum),
    likeNum: toNumber(item.likeNum),
    commentCount: toNumber(item.commentCount),
    publish: toNumber(item.publish),
    publishTime: toStringOrNull(item.publishTime),
    updatedTime: toStringOrNull(item.updatedTime) ?? '',
    auditTime: toStringOrNull(item.auditTime),
    submitTime: toStringOrNull(item.submitTime),
    func: toStringOrNull(item.func) ?? 'none',
    category: toStringOrNull(item.category),
    ref: toStringOrNull(item.ref),
    cartoonId: toStringOrNull(item.cartoonId) ?? '',
    cartoonName: toStringOrNull(item.cartoonName),
    cartoonAvatar: toStringOrNull(item.cartoonAvatar),
    relCartoons: normalizeRelCartoons(item),
    userId: toStringOrNull(item.userId) ?? normalizeUser(item.user)?.id ?? '',
    user: normalizeUser(item.user),
    ynSubscribe: typeof item.ynSubscribe === 'number' ? item.ynSubscribe : null,
    viewScope: toStringOrNull(item.viewScope),
    topics: Array.isArray(item.topics) ? item.topics : null,
    anchor,
    fromName: toStringOrNull(item.fromName),
    jump: toStringOrNull(item.jump) ?? anchor.jump,
    url: toStringOrNull(item.url),
    onlineUsers: Array.isArray(item.onlineUsers) ? item.onlineUsers : null,
    onlineNum: toNumber(item.onlineNum),
    collection: isRecord(item.collection) ? (item.collection as UserWorkItem['collection']) : null,
    extra: normalizeExtra(item.extra),
    activity: typeof item.activity === 'number' ? item.activity : null,
    showTag: isRecord(item.showTag) ? (item.showTag as UserWorkItem['showTag']) : null,
    liked: typeof item.liked === 'boolean' || typeof item.liked === 'number' ? item.liked : 0,
    permission: isRecord(item.permission) ? (item.permission as UserWorkItem['permission']) : null,
  }
}

export function normalizeSearchWorkItems(items: SearchWorkItem[] | null | undefined) {
  return (items ?? []).map(normalizeSearchWorkItem)
}

export function getSearchHotWordOptions(items: SearchHotWordItem[]) {
  const usedLabels = new Set<string>()
  const options: SearchHotWordOption[] = []

  items.forEach((item) => {
    if (item.enable === false || item.enable === 0 || item.labelType === 'img') {
      return
    }

    if (item.actionConfig?.contentType !== 'search') {
      return
    }

    const label = item.labelText?.trim()

    if (!label || usedLabels.has(label)) {
      return
    }

    usedLabels.add(label)
    options.push({
      id: item.id,
      label,
      keyword: item.actionConfig.value?.trim() || label,
    })
  })

  return options
}

export function shuffleSearchHotWords(
  items: SearchHotWordOption[],
  count: number,
): SearchHotWordOption[] {
  return [...items]
    .map((item) => ({ item, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .slice(0, count)
    .map(({ item }) => item)
}
